import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { LoadingSpinner } from './ui/loading-spinner';
import { X, Search, Plus, Trash, RefreshCw } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../hooks/useAuth'; // Import useAuth to access user profile data
import { decryptData } from '../encryptionUtil';
import { cn } from "../lib/utils";
import { usePracticeData } from '../contexts/PracticeDataContext'; // Import the context hook
import { useTheme } from './ui/theme-provider'; // Import useTheme

const PracticeSettings = ({ onClose, onSave, initialSelectedPractices = [], user }) => {
  // Get practice data from context
  const { 
    allPractices, 
    loading: practicesLoading, 
    hasUpdates,
    refreshPractices,
    enhancePractices
  } = usePracticeData();
  
  // Core state
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [lastAddedPracticeCode, setLastAddedPracticeCode] = useState(null); // Track newly added practice
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [suggestedPractices, setSuggestedPractices] = useState([]);
  const [userPostcode, setUserPostcode] = useState(null);
  
  const { toast } = useToast();
  const { getUserProfile } = useAuth();
  const { theme } = useTheme(); // Get theme

  const searchCache = useRef(new Map());
  const searchTimeout = useRef(null);

  // Initialize selected practices from props
  useEffect(() => {
    if (initialSelectedPractices?.length > 0 && allPractices.length > 0) {
      // Use the enhancePractices function from context to get full practice details
      const enhanced = enhancePractices(initialSelectedPractices);
      setSelectedPractices(enhanced);
    }
    
    // Set loading state based on allPractices
    if (allPractices.length > 0) {
      setLoading(false);
    }
  }, [initialSelectedPractices, allPractices, enhancePractices]);

  // Load user profile data to get postcode
  useEffect(() => {
    const getProfileFromLocalSources = async () => {
      if (!user?.uid) return null;
      
      try {
        console.log('Attempting to get profile data from local sources first');
        
        // First check if we have a cached user object with postcode in localStorage
        const cachedUserKey = `user_profile_${user.uid}`;
        const cachedUserData = localStorage.getItem(cachedUserKey);
        
        if (cachedUserData) {
          try {
            const parsedData = JSON.parse(cachedUserData);
            if (parsedData.postcode) {
              console.log('Using postcode from localStorage cache');
              
              // If the postcode is encrypted, decrypt it
              let postcodeValue = parsedData.postcode;
              if (postcodeValue.startsWith('U2FsdGVkX1') || postcodeValue.length > 10) {
                try {
                  postcodeValue = decryptData(postcodeValue);
                  console.log('Decrypted postcode from cache:', postcodeValue);
                } catch (decryptError) {
                  console.log('Could not decrypt postcode, using as-is');
                }
              }
              
              // Extract the first 3 characters (outward code)
              const postcodePrefix = postcodeValue.substring(0, 3).toUpperCase();
              setUserPostcode(postcodePrefix);
              return postcodePrefix;
            }
          } catch (parseError) {
            console.log('Error parsing cached user data:', parseError);
          }
        }
        
        // If user prop has postcode directly, use that
        if (user.postcode) {
          console.log('Using postcode from user prop:', user.postcode);
          
          // If the postcode is encrypted, decrypt it
          let postcodeValue = user.postcode;
          if (postcodeValue.startsWith('U2FsdGVkX1') || postcodeValue.length > 10) {
            try {
              postcodeValue = decryptData(postcodeValue);
              console.log('Decrypted postcode from user prop:', postcodeValue);
            } catch (decryptError) {
              console.log('Could not decrypt postcode, using as-is');
            }
          }
          
          // Extract the first 3 characters (outward code)
          const postcodePrefix = postcodeValue.substring(0, 3).toUpperCase();
          setUserPostcode(postcodePrefix);
          return postcodePrefix;
        }
        
        // Check if we have profile in component props
        if (user.profile && user.profile.postcode) {
          console.log('Using profile from user prop');
          
          // If the postcode is encrypted, decrypt it
          let postcodeValue = user.profile.postcode;
          if (postcodeValue.startsWith('U2FsdGVkX1') || postcodeValue.length > 10) {
            try {
              postcodeValue = decryptData(postcodeValue);
              console.log('Decrypted postcode from user profile prop:', postcodeValue);
            } catch (decryptError) {
              console.log('Could not decrypt postcode, using as-is');
            }
          }
          
          // Extract the first 3 characters (outward code)
          const postcodePrefix = postcodeValue.substring(0, 3).toUpperCase();
          setUserPostcode(postcodePrefix);
          return postcodePrefix;
        }
        
        // If nothing found locally, return null to trigger Firebase check
        return null;
      } catch (error) {
        console.error('Error getting profile from local sources:', error);
        return null;
      }
    };
    
    // First try local sources
    getProfileFromLocalSources().then(localPostcodePrefix => {
      // If we didn't find it locally, try Firebase
      if (!localPostcodePrefix && getUserProfile) {
        console.log('Loading user profile from Firebase for location-based suggestions');
        getUserProfile(user.uid).then(profile => {
          if (profile && profile.postcode) {
            // If the postcode is encrypted, decrypt it
            let postcodeValue = profile.postcode;
            if (postcodeValue.startsWith('U2FsdGVkX1') || postcodeValue.length > 10) {
              try {
                postcodeValue = decryptData(postcodeValue);
                console.log('Decrypted postcode from Firebase:', postcodeValue);
              } catch (decryptError) {
                console.log('Could not decrypt postcode from Firebase, using as-is');
              }
            }
            
            // Extract the first 3 characters (outward code)
            const postcodePrefix = postcodeValue.substring(0, 3).toUpperCase();
            setUserPostcode(postcodePrefix);
          }
        }).catch(error => {
          console.error('Error loading user profile from Firebase:', error);
        });
      }
    });
  }, [user, getUserProfile]);
  
  // Load top practices with better location-based filtering - now using allPractices from context
  useEffect(() => {
    if (allPractices.length === 0) return;
    
    // Only apply when there are no search results
    if (searchResults.length > 0 || isSearchActive) return;
    
    // Get practices with most complete data and not already selected
    const alreadySelectedCodes = new Set(selectedPractices.map(p => 
      typeof p === 'object' ? p.prac_code : p
    ));
    
    let locationBasedPractices = [];
    let postcodeBasedPractices = [];
    let townBasedPractices = [];
    
    // STEP 1: Try using profile postcode if available
    if (userPostcode) {
      console.log(`Looking for practices near user's postcode prefix: ${userPostcode}`);
      
      postcodeBasedPractices = allPractices
        .filter(p => p.postcode && p.postcode.toUpperCase().startsWith(userPostcode))
        .filter(p => !alreadySelectedCodes.has(p.prac_code))
        .slice(0, 15);
      
      console.log(`Found ${postcodeBasedPractices.length} practices matching user's postcode prefix`);
    }
    
    // STEP 2: Collect address4 values from selected practices
    const selectedTowns = new Set();
    
    // IMPROVED: Add a separate function to efficiently collect address4 data
    const collectAddress4FromPractices = () => {
      // First try to get address4 directly from selectedPractices if they have full data
      selectedPractices.forEach(practice => {
        if (typeof practice === 'object' && practice.address4) {
          selectedTowns.add(practice.address4.toUpperCase());
        }
      });
      
      // If we don't have enough towns, try to find the practices in allPractices
      if (selectedTowns.size === 0) {
        const practiceCodes = selectedPractices.map(p => 
          typeof p === 'object' ? p.prac_code : p
        );
        
        // Find these practices in the full dataset to get their address4
        practiceCodes.forEach(code => {
          const fullPractice = allPractices.find(p => p.prac_code === code);
          if (fullPractice && fullPractice.address4) {
            selectedTowns.add(fullPractice.address4.toUpperCase());
          }
        });
      }
      
      return selectedTowns;
    };
    
    // Collect all towns in one go
    collectAddress4FromPractices();
    
    // If we have towns, find practices in those towns
    if (selectedTowns.size > 0) {
      console.log(`Looking for practices in towns: ${Array.from(selectedTowns).join(', ')}`);
      
      townBasedPractices = allPractices
        .filter(p => p.address4 && selectedTowns.has(p.address4.toUpperCase()))
        .filter(p => !alreadySelectedCodes.has(p.prac_code));
      
      console.log(`Found ${townBasedPractices.length} practices in the same towns as selections`);
    }
    
    // IMPROVED: Combine both data sources with prioritization
    // Rather than choosing one source or the other, we'll combine them
    
    // First add practices from postal code area (user's location)
    locationBasedPractices = [...postcodeBasedPractices];
    
    // Then add practices from selected towns that aren't already in the list
    const existingPracticeCodes = new Set(locationBasedPractices.map(p => p.prac_code));
    
    townBasedPractices.forEach(practice => {
      if (!existingPracticeCodes.has(practice.prac_code)) {
        locationBasedPractices.push(practice);
        existingPracticeCodes.add(practice.prac_code);
      }
    });
    
    // STEP 3: If we still don't have enough, fall back to top practices from JSON
    if (locationBasedPractices.length < 15) {
      console.log('Adding more default practices to suggestions');
      
      const defaultPractices = allPractices
        .filter(p => p.prac_name && p.address1 && p.address4 && p.postcode)
        .filter(p => !alreadySelectedCodes.has(p.prac_code) && !existingPracticeCodes.has(p.prac_code))
        .slice(0, 25 - locationBasedPractices.length); // Fill up to 25 total
      
      // Add these to our location-based practices
      locationBasedPractices = [...locationBasedPractices, ...defaultPractices];
      
      console.log(`Added ${defaultPractices.length} default practices to suggestions`);
    }
    
    // Limit to top 25 suggestions
    locationBasedPractices = locationBasedPractices.slice(0, 25);
    
    console.log(`Final suggestion list contains ${locationBasedPractices.length} practices`);
    
    // Set the suggested practices
    setSuggestedPractices(locationBasedPractices);
    
  }, [allPractices, selectedPractices, userPostcode, searchResults.length, isSearchActive]);

  // Debug logs
  useEffect(() => {
    console.log('PracticeSettings props & state:', {
      initialSelectedCount: initialSelectedPractices?.length || 0,
      contextPracticesCount: allPractices?.length || 0,
      selectedPracticesCount: selectedPractices.length
    });
  }, [initialSelectedPractices, allPractices, selectedPractices]);

  // Handle manual refresh of practice data
  const handleRefreshPractices = async () => {
    if (refreshing) return;
    
    setRefreshing(true);
    
    try {
      // Clear search cache
      searchCache.current.clear();
      
      // Use the refreshPractices function from context
      await refreshPractices(true);
      
      // Update selected practices to ensure they have latest data
      if (selectedPractices.length > 0) {
        const refreshedSelected = enhancePractices(selectedPractices);
        setSelectedPractices(refreshedSelected);
      }
    } catch (error) {
      console.error('Error refreshing practices:', error);
      toast({
        title: "Refresh Failed",
        description: "Could not refresh practice data",
        variant: "destructive"
      });
    } finally {
      setRefreshing(false);
    }
  };

  // Enhanced search function to search by multiple fields - updated to use allPractices from context
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setIsSearchActive(term.length > 0);
    setSearching(true); // Indicate searching has started

    // Debounce search
    clearTimeout(searchTimeout.current);

    if (term.length < 2) {
      setSearchResults([]);
      setSearching(false);
      searchCache.current.clear(); // Clear cache for short terms
      return;
    }

    const lowerCaseTerm = term.toLowerCase();

    // Check cache first
    if (searchCache.current.has(lowerCaseTerm)) {
      setSearchResults(searchCache.current.get(lowerCaseTerm));
      setSearching(false);
      return;
    }

    searchTimeout.current = setTimeout(() => {
      const searchTerms = lowerCaseTerm.split(/\s+/).filter(Boolean); // Split by space and remove empty strings

      const results = (allPractices || []).filter(practice => {
        // Combine relevant fields into a single searchable string
        const searchableText = [
          practice.prac_name,
          practice.address1,
          practice.address2,
          practice.address3,
          practice.address4, // Town/City
          practice.address5, // County
          practice.postcode?.replace(/\s+/g, '') // Postcode without spaces
        ].join(' ').toLowerCase();

        // Check if *every* search term is included in the combined text
        return searchTerms.every(searchTerm => searchableText.includes(searchTerm));
      });

      searchCache.current.set(lowerCaseTerm, results); // Cache the result
      setSearchResults(results);
      setSearching(false); // Indicate searching is complete
    }, 300); // 300ms debounce
  };

  // Handle adding a practice
  const handleAddPractice = (practice) => {
    const isAlreadySelected = selectedPractices.some(p => p.prac_code === practice.prac_code);
    
    if (isAlreadySelected) {
      return;
    }
    
    // IMPROVED: Include address4 and postcode when adding practice
    const practiceWithDetails = {
      prac_code: practice.prac_code,
      prac_name: practice.prac_name || '',
      address4: practice.address4 || '',
      postcode: practice.postcode || '',
      address1: practice.address1 || ''
    };
    
    const updatedPractices = [...selectedPractices, practiceWithDetails];
    setSelectedPractices(updatedPractices);
    
    // Track this as the newly added practice for auto-selection
    setLastAddedPracticeCode(practice.prac_code);
    
    // Clear search
    setSearchTerm('');
    setSearchResults([]);
    setIsSearchActive(false);
  };

  // Handle removing a practice
  const handleRemovePractice = (practiceToRemove) => {
    const updatedPractices = selectedPractices.filter(
      practice => practice.prac_code !== practiceToRemove.prac_code
    );
    setSelectedPractices(updatedPractices);
  };

  // Handle save button click
  const handleSave = () => {
    // Pass both the practices and the last added practice code
    onSave(selectedPractices, lastAddedPracticeCode);
  };

  // Show loading spinner while practices are loading
  if (loading || practicesLoading) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        {/* DialogContent: Theme-aware */}
        <DialogContent className={cn(
          "sm:max-w-md md:max-w-lg max-h-[90vh] flex flex-col overflow-hidden",
          theme === 'light' ? 'bg-white text-black' : 'bg-[#2A2A3F] text-white'
        )}>
          {/* DialogHeader: Theme-aware */}
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className={cn(theme === 'light' ? 'text-black' : 'text-purple-300')}>Practice Selection</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center items-center p-10">
            <LoadingSpinner size="large" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      {/* DialogContent: Theme-aware */}
      <DialogContent className={cn(
        "sm:max-w-md md:max-w-lg h-[90vh] md:max-h-[90vh] flex flex-col overflow-hidden",
        theme === 'light' ? 'bg-white text-black' : 'bg-[#2A2A3F] text-white'
      )}>
        {/* DialogHeader: Theme-aware */}
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle className={cn(
            "text-lg md:text-xl",
            theme === 'light' ? 'text-black' : 'text-purple-300'
          )}>
            Practice Selection
          </DialogTitle>
          {/* Refresh Button: Theme-aware */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefreshPractices}
            disabled={refreshing}
            className={cn(
              hasUpdates
                ? (theme === 'light' ? 'text-amber-600 hover:text-amber-700' : 'text-amber-300 hover:text-amber-200')
                : (theme === 'light' ? 'text-purple-600 hover:text-purple-700' : 'text-purple-300 hover:text-purple-100')
            )}
            title="Refresh practice data from server"
          >
            {refreshing ? <LoadingSpinner size="small" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </DialogHeader>
        {/* DialogDescription: Theme-aware */}
        <DialogDescription className={cn(
          "text-xs md:text-sm",
          theme === 'light' ? 'text-gray-600' : 'text-gray-300'
        )}>
          Search and select the practices you work with.<br />
          If the name of your practice is not listed or is not named properly please email{" "}
          <a 
            href="mailto:support@coooly.co.uk?subject=Practice%20issue" 
            className={cn(
              "underline font-medium hover:opacity-80 transition-opacity",
              theme === 'light' ? 'text-purple-700' : 'text-purple-300'
            )}
          >
            support@coooly.co.uk
          </a>
        </DialogDescription>

        <div className="flex flex-col flex-grow space-y-4 overflow-hidden">
          {/* Search box: Theme-aware */}
          <div className="relative">
            {/* Use Flexbox for light mode layout */}
            {theme === 'light' ? (
              <div className="flex items-center w-full h-9 rounded-md border border-gray-300 bg-white px-3"> 
                {/* Icon with right margin */}
                <Search className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" /> 
                {/* Input takes remaining space, no border/bg needed */}
                <input
                  type="text"
                  className="flex-grow h-full text-xs md:text-sm text-black placeholder-gray-400 bg-transparent focus:outline-none" 
                  placeholder="Search by name, address, town, or postcode..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
            ) : (
              // Dark mode remains the same (relative container with absolute icon + Input component)
              <div className="relative h-9"> 
                <Search className={cn(
                  "absolute left-2 top-2.5 h-3 w-3 md:h-4 md:w-4 z-10", 
                  'text-gray-400'
                )} />
                <Input
                  className="pl-7 md:pl-8 text-xs md:text-sm h-9 bg-[#1E1E2D] border-gray-600 text-white placeholder-gray-500" 
                  placeholder="Search by name, address, town, or postcode..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
            )}
          </div>

          {/* Results area: Theme-aware */}
          <div className={cn(
            "flex-grow overflow-hidden border rounded-md",
            theme === 'light' ? 'bg-gray-50 border-gray-200' : 'bg-[#1E1E2D] border-gray-600'
          )}>
            {/* Header: Theme-aware */}
            <h3 className={cn(
              "text-xs md:text-sm font-medium p-2 border-b",
              theme === 'light' ? 'text-gray-800 border-gray-200' : 'text-white border-gray-600'
            )}>
              {isSearchActive && searchResults.length > 0
                ? `Search Results (${searchResults.length})`
                : "Suggested Practices"}
            </h3>

            {/* ScrollArea: Theme-aware */}
            <ScrollArea className="h-[calc(100%-2rem)] pr-2">
              {searching ? (
                <div className="p-2 text-center">
                  <LoadingSpinner size="small" />
                </div>
              ) : isSearchActive && searchResults.length > 0 ? (
                <ul className="p-2 space-y-1.5">
                  {searchResults.map(practice => (
                    // List Item: Theme-aware
                    <li
                      key={practice.prac_code}
                      className={cn(
                        "hover:bg-gray-700/50 cursor-pointer border rounded-md p-1.5 flex items-center justify-between",
                        theme === 'light'
                          ? 'bg-white hover:bg-gray-100 border-gray-200'
                          : 'bg-[#2A2A3F] hover:bg-gray-700/50 border-gray-700'
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        {/* Text: Theme-aware */}
                        <div className={cn(
                          "font-medium text-xs md:text-sm truncate",
                          theme === 'light' ? 'text-black' : 'text-white'
                        )}>{practice.prac_name}</div>
                        <div className={cn(
                          "text-xs truncate",
                          theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                        )}>
                          {practice.address1}{practice.address4 ? `, ${practice.address4}` : ''} {practice.postcode}
                        </div>
                      </div>
                      {/* Add Button: Consistent purple */}
                      <button
                        onClick={() => handleAddPractice(practice)}
                        className="ml-2 bg-purple-600 hover:bg-purple-700 p-1 rounded border border-purple-400 flex items-center justify-center"
                        title="Add practice"
                      >
                        <Plus className="h-3 w-3 md:h-4 md:w-4 text-white" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : isSearchActive ? (
                 // No Results Text: Theme-aware
                <div className={cn(
                  "p-2 text-center text-xs",
                  theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                )}>No practices found</div>
              ) : (
                // Suggested Practices List: Theme-aware
                <ul className="p-2 space-y-1.5">
                  {suggestedPractices.map(practice => (
                    <li
                      key={practice.prac_code}
                      className={cn(
                        "hover:bg-gray-700/50 cursor-pointer border rounded-md p-1.5 flex items-center justify-between",
                         theme === 'light'
                          ? 'bg-white hover:bg-gray-100 border-gray-200'
                          : 'bg-[#2A2A3F] hover:bg-gray-700/50 border-gray-700'
                      )}
                    >
                      <div className="flex-1 min-w-0">
                         {/* Text: Theme-aware */}
                        <div className={cn(
                          "font-medium text-xs md:text-sm truncate",
                           theme === 'light' ? 'text-black' : 'text-white'
                        )}>{practice.prac_name}</div>
                        <div className={cn(
                          "text-xs truncate",
                           theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                        )}>
                          {practice.address4 && `${practice.address4}, `}
                          {practice.postcode}
                        </div>
                      </div>
                       {/* Add Button: Consistent purple */}
                      <button
                        onClick={() => handleAddPractice(practice)}
                        className="ml-2 bg-purple-600 hover:bg-purple-700 p-1 rounded border border-purple-400 flex items-center justify-center"
                        title="Add practice"
                      >
                        <Plus className="h-3 w-3 md:h-4 md:w-4 text-white" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </ScrollArea>
          </div>

          {/* Selected practices box: Theme-aware */}
          <div className={cn(
            "border rounded-md p-2 mb-2",
            theme === 'light' ? 'bg-gray-50 border-gray-200' : 'bg-[#1E1E2D] border-gray-600'
          )}>
             {/* Header: Theme-aware */}
            <h3 className={cn(
              "text-xs md:text-sm font-medium mb-1.5",
              theme === 'light' ? 'text-gray-800' : 'text-white'
            )}>
              Selected Practices ({selectedPractices.length})
            </h3>

            {selectedPractices.length === 0 ? (
              // No Selected Text: Theme-aware
              <div className={cn(
                "text-center py-4 text-xs",
                theme === 'light' ? 'text-gray-500' : 'text-gray-400'
              )}>
                No practices selected
              </div>
            ) : (
              <div className="max-h-60 overflow-y-auto pr-2">
                <ul className="space-y-1.5">
                  {selectedPractices.map(practice => (
                    // List Item: Theme-aware
                    <li
                      key={practice.prac_code}
                      className={cn(
                        "border rounded-md p-1.5 flex justify-between items-center",
                        theme === 'light'
                          ? 'bg-white border-gray-200'
                          : 'bg-[#2A2A3F] border-gray-700'
                      )}
                    >
                      <div className="flex-1 min-w-0">
                         {/* Text: Theme-aware */}
                        <div className={cn(
                          "font-medium text-xs md:text-sm truncate",
                           theme === 'light' ? 'text-black' : 'text-white'
                        )}>{practice.prac_name}</div>
                        <div className={cn(
                          "text-xs truncate",
                           theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                        )}>
                          {practice.address4 && `${practice.address4}, `}{practice.postcode}
                        </div>
                      </div>
                       {/* Remove Button: Consistent red */}
                      <button
                        onClick={() => handleRemovePractice(practice)}
                        className="ml-2 bg-red-600 hover:bg-red-700 p-1 rounded border border-red-400 flex items-center justify-center"
                        title="Remove practice"
                      >
                        <Trash className="h-3 w-3 md:h-4 md:w-4 text-white" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* DialogFooter: Theme-aware border */}
        <DialogFooter className={cn(
          "flex justify-end pt-2 border-t mt-auto",
          theme === 'light' ? 'border-gray-200' : 'border-gray-700'
        )}>
          <div className="w-full flex space-x-2 justify-end">
            {/* Cancel Button: Theme-aware outline */}
            <Button
              variant="outline"
              onClick={onClose}
              className={cn(
                "flex-1 md:flex-none md:min-w-[100px] text-xs md:text-sm",
                theme === 'light'
                  ? 'border-gray-300 text-gray-700 hover:bg-gray-100'
                  : 'border-gray-600 text-white hover:bg-gray-700'
              )}
            >
              Cancel
            </Button>
            {/* Save Button: Consistent purple */}
            <Button
              onClick={handleSave}
              className="flex-1 md:flex-none md:min-w-[100px] bg-purple-600 hover:bg-purple-700 text-white text-xs md:text-sm"
            >
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PracticeSettings;
